import React, { useState } from "react";
import { ComponentProps, Case } from "../types/types";
import { supabaseService } from "../SupabaseService";
import { User } from "lucide-react";

const KlarTilFakturering: React.FC<ComponentProps> = ({
  cases,
  setCases,
  openCaseForEditing,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredCases = cases.filter(
    (case_) =>
      case_.kategori === "Klar til fakturering" &&
      (case_.kunde.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.beskrivelse.toLowerCase().includes(searchTerm.toLowerCase()))
  );

const groupedCases = filteredCases.reduce((groups, case_) => {
  const customerName = case_.kunde;
  if (!groups[customerName]) {
    groups[customerName] = [];
  }
  groups[customerName].push(case_);
  return groups;
}, {} as Record<string, typeof filteredCases>);

  const handleEditCase = async (case_: Case) => {
    // Bare åpne for redigering, IKKE flytt til ny-registrering
    openCaseForEditing(case_);
  };

  const moveToArchive = async (caseId: number) => {
    setIsSaving(true);
    setError(null);
    
    const originalCases = [...cases];
    
    setCases((prev) =>
      prev.map((case_) =>
        case_.id === caseId
          ? { ...case_, kategori: "Arkiv", opprettet: new Date().toISOString() }
          : case_
      )
    );

    try {
      const caseToUpdate = cases.find(c => c.id === caseId);
      if (!caseToUpdate) throw new Error("Case ikke funnet");

      const saved = await supabaseService.saveCase({
        ...caseToUpdate,
        kategori: "Arkiv",
        opprettet: new Date().toISOString()
      });

      if (!saved) {
        throw new Error("Kunne ikke lagre til Supabase");
      }
    } catch (error) {
      setCases(originalCases);
      setError("Kunne ikke arkivere. Prøv igjen.");
      console.error("Archive error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveSubmit = () => {
    if (selectedCaseId != null) {
      moveToArchive(selectedCaseId);
      setShowArchiveDialog(false);
      setSelectedCaseId(null);
    }
  };

  const openArchiveDialog = (caseId: number) => {
    setSelectedCaseId(caseId);
    setShowArchiveDialog(true);
  };

  const getDaysInCategory = (case_: Case): number => {
    const caseDate = new Date(case_.opprettet);
    const now = new Date();
    return (
      Math.floor((now.getTime() - caseDate.getTime()) / (1000 * 60 * 60 * 24)) +
      1
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
          <span className="mr-2">⚠️</span>
          {error}
        </div>
      )}

      {/* Header & Search */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Klar til fakturering ({filteredCases.length})
        </h3>
        <input
          type="text"
          placeholder="Søk i saker..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
      </div>

      {/* Archive Dialog */}
      {showArchiveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Arkiver sak
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Er du sikker på at denne saken er ferdig behandlet og klar for
                arkivering?
              </p>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <strong className="text-gray-800">
                  Dette vil flytte saken til arkiv
                </strong>
                <ul className="text-sm text-gray-600 space-y-1 mt-2">
                  <li>• Saken merkes som ferdig behandlet</li>
                  <li>• Dagtelleren nullstilles</li>
                  <li>• Saken vises kun i arkiv-fanen</li>
                </ul>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleArchiveSubmit}
                  disabled={isSaving}
                  className={`flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 font-medium ${
                    isSaving ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center">
                      <span className="animate-spin mr-2">⌛</span>
                      Arkiverer...
                    </span>
                  ) : (
                    '📁 Arkiver sak'
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowArchiveDialog(false);
                    setSelectedCaseId(null);
                  }}
                  disabled={isSaving}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 font-medium"
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cases List */}
        <div className="space-y-4">
          {Object.entries(groupedCases).map(([customerName, customerCases]) => (
            <div key={customerName} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Kunde header */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <User className="mr-2" size={18} />
                    {customerName}
                  </h3>
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                    {customerCases.length} {customerCases.length === 1 ? 'ordre' : 'ordre'}
                  </span>
                </div>
              </div>

              {/* Ordre for denne kunden */}
              <div className="divide-y divide-gray-200">
                {customerCases.map((case_) => {
                  const days = getDaysInCategory(case_);
                  return (
                    <div
                      key={case_.id}
                      className="p-4 hover:bg-gray-50 transition relative"
                    >
                      {/* Klikkbar innhold */}
                      <div
                        onClick={() => handleEditCase(case_)}
                        className="cursor-pointer"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs text-gray-500">#{case_.id}</span>
                              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                📅 {days} {days === 1 ? 'dag' : 'dager'}
                              </span>
                            </div>
                            <p className="font-medium">{case_.maskinMerke}</p>
                            <p className="text-sm text-gray-600">{case_.beskrivelse}</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openArchiveDialog(case_.id);
                            }}
                            className="ml-2 px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                          >
                            Arkiver
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
    </div>
  );
};

export default KlarTilFakturering;